import { useState, useEffect } from "react";
import { api } from "../context/AuthContext";
import { classifyApiError } from "./useApiList";
import { reconcileCounts, badgeDecision, badgeChangementActeur } from "./unreadReconcile";
import { setAppBadge as setNativeAppBadge } from "../native/badge";

/**
 * Shared unread-counts poller.
 *
 * BEFORE: every component calling useUnreadCounts started its own 60s timer.
 *         DesktopHeader + BottomNavigation + MobileHeader = 3 concurrent
 *         fetchers hitting /messages/unread-count and /notifications/unread-count
 *         every minute, per tab. On a typical visit this flooded the logs.
 *
 * AFTER:  one module-level poller per user session. Subscribers get the
 *         current value from a singleton store. Polling is suspended when
 *         the tab is hidden and resumed on visibility change.
 */

const subscribers = new Set();
const state = {
  unreadMessages: 0,
  unreadNotifications: 0,
  pendingActions: 0, // ventes à expédier + achats à confirmer (onglet « Mon espace »)
  // Au moins un compteur n'a pas pu être relu au dernier tour : les valeurs
  // affichées sont les DERNIÈRES CONNUES, pas des valeurs fraîches.
  stale: false,
  userId: null,
  timer: null,
  visibilityListener: null,
  refreshListener: null,
  messagesReadListener: null,
  inFlight: false,
};

/**
 * ÉCRIVAIN UNIQUE du badge système (PWA + icône native). Deux surfaces, une
 * seule décision — avant le 31/08/2026 il y avait DEUX écrivains : ici pour le
 * badge PWA, et NotificationBell.js pour le badge natif, qui l'effaçait à
 * CHAQUE montage avec la valeur initiale 0, avant tout appel réseau. Le badge
 * natif était donc remis à zéro à chaque démarrage de l'app, panne ou pas.
 *
 * `total === null` = ON NE SAIT PAS -> on n'écrit RIEN (ni set, ni clear).
 * C'est la seule façon de ne pas détruire le compteur laissé sur l'icône par
 * la session précédente alors qu'on n'a pas encore lu le serveur.
 */
const updateBadge = (total) => {
  if (total === null || total === undefined) return;
  try {
    if ("setAppBadge" in navigator) {
      if (total > 0) navigator.setAppBadge(total);
      else navigator.clearAppBadge();
    }
  } catch {}
  // No-op sur le web (garde isNativeSync), plugin Capacitor sur mobile.
  try { setNativeAppBadge(total); } catch {}
};

/**
 * Pousse le compteur de notifications au SERVICE WORKER — et UNIQUEMENT une
 * valeur PROUVEE.
 *
 * Le worker (public/sw.js) sonde /notifications/unread-count toutes les 30 s et
 * affiche une push systeme quand le compteur AUGMENTE. Il a besoin d'une
 * REFERENCE pour savoir ce qui est « nouveau ». Le recepteur SET_NOTIF_COUNT
 * existait depuis toujours dans sw.js, mais AUCUN emetteur ne l'appelait dans
 * tout src/ : branchement mort. Le worker devait donc deviner sa reference, et
 * devinait 0 — d'ou une push « Vous avez N nouvelles notifications » pour un
 * backlog deja vu, a chaque redemarrage du worker par le navigateur.
 *
 * On n'envoie JAMAIS un « je ne sais pas » : une valeur non relue laisserait le
 * worker sur sa propre reference, ce qui est le comportement correct.
 */
const pousserCompteurAuSW = (count) => {
  if (typeof count !== "number" || !Number.isFinite(count) || count < 0) return;
  try {
    navigator.serviceWorker?.controller?.postMessage({
      type: "SET_NOTIF_COUNT",
      count,
    });
  } catch {}
};

const notify = () => {
  for (const cb of subscribers) {
    try {
      cb({
        unreadMessages: state.unreadMessages,
        unreadNotifications: state.unreadNotifications,
        pendingActions: state.pendingActions,
        countsStale: state.stale,
      });
    } catch {}
  }
};

/**
 * Transforme une promesse axios en OUTCOME explicite. Aucune valeur n'est
 * fabriquee : soit on a lu ({ok:true}), soit on ne sait pas ({ok:false}).
 * C'est le remplacement du `.catch(() => ({ data: { count: 0 } }))` qui
 * confondait « tout est lu » avec « je n'ai pas pu lire ».
 */
const outcome = (promesse) =>
  promesse
    .then((r) => ({ ok: true, value: r?.data }))
    .catch((e) => ({ ok: false, kind: classifyApiError(e) }));

const fetchCounts = async () => {
  if (!state.userId || document.hidden || state.inFlight) return;
  state.inFlight = true;
  try {
    const [messages, notifications, actions] = await Promise.all([
      outcome(api.get("/messages/unread-count")),
      outcome(api.get("/notifications/unread-count")),
      outcome(api.get("/transactions/actions-pending-count")),
    ]);
    const { next, stale, badge, change, inconnus } = reconcileCounts(state, {
      messages,
      notifications,
      actions,
    });
    // `stale` fait partie de l'instantane envoye aux abonnes : s'il change, il
    // faut notifier MEME si aucune valeur n'a bouge — sinon on exposerait un
    // drapeau perime, c'est-a-dire exactement le defaut que ce lot corrige.
    const staleChange = state.stale !== stale;
    state.stale = stale;
    // Le worker ne doit jamais avoir a deviner sa reference : on la lui donne
    // des qu'on l'a PROUVEE (et seulement dans ce cas).
    if (!inconnus.includes("notifications")) {
      pousserCompteurAuSW(next.unreadNotifications);
    }
    // Le badge s'ecrit meme sans changement de valeur : au tout premier fetch
    // reussi, `next` peut egaler l'etat initial (0) alors que l'icone porte
    // encore le compteur de la session precedente — il faut alors l'effacer.
    updateBadge(badge);
    if (change) {
      state.unreadMessages = next.unreadMessages;
      state.unreadNotifications = next.unreadNotifications;
      state.pendingActions = next.pendingActions;
    }
    if (change || staleChange) notify();
  } catch {} finally {
    state.inFlight = false;
  }
};

const startPolling = (userId) => {
  // Reset state when user changes
  if (state.userId !== userId) {
    // Décidé AVANT la réassignation, et par une règle pure testée : au premier
    // montage l'ancien id est `null` — il ne faut alors RIEN écrire, surtout
    // pas `0` (cf. badgeChangementActeur).
    const badgeActeur = badgeChangementActeur(state.userId, userId);
    state.userId = userId;
    state.unreadMessages = 0;
    state.unreadNotifications = 0;
    state.pendingActions = 0;
    state.stale = false;
    updateBadge(badgeActeur);
  }
  if (state.timer) return;
  if (!userId) return;

  fetchCounts();
  // Polling 3 min (etait 60s) : suffisamment frais pour les badges non lus,
  // ~3x moins de charge serveur + moins de bruit dans les logs. Le poller
  // se reveille deja instantanement au retour sur l'onglet (visibilitychange).
  state.timer = setInterval(fetchCounts, 180000);
  state.visibilityListener = () => { if (!document.hidden) fetchCounts(); };
  document.addEventListener("visibilitychange", state.visibilityListener);
  // Rafraîchissement IMMÉDIAT à la demande (ex. ouverture d'une conversation =
  // messages marqués lus côté serveur → la surbrillance doit s'éteindre tout de
  // suite, sans attendre le poll de 180 s). Émis via window dispatchEvent.
  state.refreshListener = () => fetchCounts();
  window.addEventListener("gively:refresh-unread", state.refreshListener);

  // Décrément OPTIMISTE : à l'ouverture d'une conversation, la pastille messagerie
  // tombe à 0 IMMÉDIATEMENT (le poll/refresh réconcilie la vérité ensuite). Émis
  // par MessagesPage via window.dispatchEvent("gively:messages-read").
  state.messagesReadListener = () => {
    if (state.unreadMessages !== 0) {
      state.unreadMessages = 0;
      // `updateBadge(0)` effaçait AUSSI les notifications non lues : ouvrir une
      // conversation éteignait le badge système en entier. Asymétrie avec
      // clearNotifications, qui préservait déjà l'autre compteur.
      updateBadge(badgeDecision({ messages: 0, notifications: state.unreadNotifications }));
      notify();
    }
  };
  window.addEventListener("gively:messages-read", state.messagesReadListener);
};

const stopPolling = () => {
  if (state.timer) { clearInterval(state.timer); state.timer = null; }
  if (state.visibilityListener) {
    document.removeEventListener("visibilitychange", state.visibilityListener);
    state.visibilityListener = null;
  }
  if (state.refreshListener) {
    window.removeEventListener("gively:refresh-unread", state.refreshListener);
    state.refreshListener = null;
  }
  if (state.messagesReadListener) {
    window.removeEventListener("gively:messages-read", state.messagesReadListener);
    state.messagesReadListener = null;
  }
  state.userId = null;
  state.unreadMessages = 0;
  state.unreadNotifications = 0;
  state.pendingActions = 0;
  state.stale = false;
  // Déconnexion : second « clear » légitime sans réponse serveur. Avant le
  // 31/08/2026, stopPolling remettait l'état à 0 SANS toucher au badge — le
  // badge PWA de l'ancien compte restait allumé après un logout.
  updateBadge(0);
};

export const useUnreadCounts = (user) => {
  const [snapshot, setSnapshot] = useState({
    unreadMessages: state.unreadMessages,
    unreadNotifications: state.unreadNotifications,
    pendingActions: state.pendingActions,
    countsStale: state.stale,
  });

  useEffect(() => {
    const onUpdate = (s) => setSnapshot(s);
    subscribers.add(onUpdate);

    // Actor-agnostique (parité User↔Association) : l'acteur peut être un USER
    // (user_id/id) OU une ASSOCIATION (account_id/association_id). Les endpoints
    // pollés sont multi-acteur côté backend (notifications/unread-count via
    // get_current_user_or_association ; les endpoints user-only retombent à 0 via
    // .catch). Un seul acteur est connecté à la fois → pas de conflit de singleton.
    const userId = user?.user_id || user?.id || user?.account_id || user?.association_id || null;
    if (userId) {
      startPolling(userId);
    } else {
      // If no subscriber has a user anymore we stop the timer.
      // (subscribers that do have a user keep it running.)
      const anySubscriberHasUser = state.userId;
      if (!anySubscriberHasUser) stopPolling();
    }

    return () => {
      subscribers.delete(onUpdate);
      if (subscribers.size === 0) stopPolling();
    };
  }, [user]);

  const clearNotifications = () => {
    if (state.unreadNotifications === 0) return;
    state.unreadNotifications = 0;
    // L'utilisateur vient de LIRE : zéro prouvé, le worker doit le savoir tout
    // de suite, sinon sa prochaine lecture verrait une « hausse » vers 0.
    pousserCompteurAuSW(0);
    updateBadge(state.unreadMessages);
    notify();
  };

  // Décrément OPTIMISTE du badge messagerie à l'ouverture d'une conversation :
  // la pastille tombe à 0 IMMÉDIATEMENT (comme les notifications), sans attendre
  // les deux allers-retours serveur. Le polling réconcilie ensuite la vérité.
  const clearMessages = () => {
    if (state.unreadMessages === 0) return;
    state.unreadMessages = 0;
    // Même correctif que messagesReadListener : on préserve les notifications.
    updateBadge(badgeDecision({ messages: 0, notifications: state.unreadNotifications }));
    notify();
  };

  return {
    unreadMessages: snapshot.unreadMessages,
    unreadNotifications: snapshot.unreadNotifications,
    pendingActions: snapshot.pendingActions,
    // Les compteurs affichés sont les dernières valeurs CONNUES (au moins une
    // source n'a pas pu être relue). Exposé pour un éventuel indicateur ; la
    // barre de navigation, elle, ne doit pas se charger d'un état d'erreur
    // (règle « esthétisme d'abord ») — elle conserve simplement la valeur.
    countsStale: snapshot.countsStale,
    clearNotifications,
    clearMessages,
  };
};
